import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SubscriptionsContext } from "../../../store/SubscriptionsContext";
import { SubscriptionTier } from "../../../../../api/tiers";
import {
  TierForm,
  TIER_FORM_ID,
  type TierFormData,
  type TierFormHandle,
} from "../components/TierForm";
import {
  StepNavPanel,
  type StepNavPanelHandle,
} from "../../../modals/StepNavPanel";
import "../../../modals/Modal.css";

function errorMessageFromUnknown(err: unknown, fallback: string): string {
  const msg = (err as { response?: { data?: { message?: string } } })?.response
    ?.data?.message;
  if (typeof msg === "string" && msg) return msg;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

type EditTierResult =
  | {
      kind: "update-success";
      detail: string;
    }
  | { kind: "update-error"; message: string }
  | { kind: "delete-success"; tierName: string }
  | { kind: "delete-error"; message: string };

const FORM_STEP = 0;
const UNSAVED_STEP = 1;
const DELETE_CONFIRM_STEP = 2;
const RESULT_STEP = 3;

export type EditTierModalBodyProps = {
  tierId: string;
  onClose: () => void;
};

export function EditTierModalBody({ tierId, onClose }: EditTierModalBodyProps) {
  const { subscriptions, tiers, updateTier, deleteTier } =
    useContext(SubscriptionsContext);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [result, setResult] = useState<EditTierResult | null>(null);
  const stepNavRef = useRef<StepNavPanelHandle>(null);
  const tierFormRef = useRef<TierFormHandle>(null);
  const cachedTierRef = useRef<SubscriptionTier | null>(null);
  const prevOpenedTierIdRef = useRef<string | undefined>(undefined);

  const activeTier = useMemo(() => {
    const fromFlat = tiers.find((tier) => tier.id === tierId);
    if (fromFlat) return fromFlat;
    for (const subscription of subscriptions) {
      const nested = subscription.tiers.find((tier) => tier.id === tierId);
      if (nested) return nested;
    }
    return undefined;
  }, [tiers, subscriptions, tierId]);

  useEffect(() => {
    if (activeTier) {
      cachedTierRef.current = activeTier;
    }
  }, [activeTier]);

  const effectiveTier = activeTier ?? cachedTierRef.current;

  const activeSubscription =
    subscriptions.find((sub) => sub.id === effectiveTier?.subscriptionId) ??
    subscriptions.find((sub) =>
      sub.tiers.some((tier) => tier.id === effectiveTier?.id),
    );

  useEffect(() => {
    if (tierId !== prevOpenedTierIdRef.current) {
      prevOpenedTierIdRef.current = tierId;
      stepNavRef.current?.goToStep(FORM_STEP);
      setResult(null);
    }
  }, [tierId]);

  const handleRequestDelete = useCallback(() => {
    stepNavRef.current?.goToStep(DELETE_CONFIRM_STEP);
  }, []);

  const handleBackFromDeleteConfirm = useCallback(() => {
    stepNavRef.current?.goToStep(FORM_STEP);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    const tier = effectiveTier;
    if (!tier || isDeleting) return;
    setIsDeleting(true);
    try {
      await deleteTier(tier.id);
      setResult({
        kind: "delete-success",
        tierName: tier.name,
      });
      stepNavRef.current?.goToStep(RESULT_STEP);
    } catch (err: unknown) {
      setResult({
        kind: "delete-error",
        message: errorMessageFromUnknown(err, "Failed to delete tier."),
      });
      stepNavRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsDeleting(false);
    }
  }, [deleteTier, effectiveTier, isDeleting]);

  const handleCloseFromResult = useCallback(() => {
    setResult(null);
    stepNavRef.current?.goToStep(FORM_STEP);
    onClose();
  }, [onClose]);

  async function handleSubmit(tierData: TierFormData) {
    if (isSubmitting || !effectiveTier) return;
    setIsSubmitting(true);

    try {
      await updateTier(effectiveTier.id, {
        name: tierData.name,
        price: Number(tierData.price),
        oldCsaMemberPrice: Number(tierData.oldCsaMemberPrice),
        spots:
          tierData.spots !== undefined ? Number(tierData.spots) : undefined,
        dependsOn: tierData.dependsOn,
        takesFrom: tierData.takesFrom,
      });

      setResult({
        kind: "update-success",
        detail: tierData.name
          ? `${tierData.name} has been updated successfully!`
          : "The tier was updated successfully!",
      });
      stepNavRef.current?.goToStep(RESULT_STEP);
    } catch (err: unknown) {
      setResult({
        kind: "update-error",
        message: errorMessageFromUnknown(err, "Failed to update tier."),
      });
      stepNavRef.current?.goToStep(RESULT_STEP);
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleCancel() {
    onClose();
  }

  if (!effectiveTier || !activeSubscription) {
    return (
      <div className="edit-inputs modal-result-step">
        <p className="modal-result-title">Could not load tier</p>
        <p>
          The selected tier data is unavailable. Please close this modal and try
          again.
        </p>
      </div>
    );
  }

  const resultStepContent =
    result?.kind === "update-success" ? (
      <div className="modal-result-step">
        <p className="modal-result-title">Updated successfully!</p>
        <p>{result.detail}</p>
      </div>
    ) : result?.kind === "delete-success" ? (
      <div className="edit-inputs modal-result-step">
        <p className="modal-result-title">Deleted</p>
        <p>
          {result.tierName
            ? `"${result.tierName}" was deleted successfully.`
            : "The tier was deleted successfully."}
        </p>
      </div>
    ) : result?.kind === "update-error" || result?.kind === "delete-error" ? (
      <div className="edit-inputs modal-result-step">
        <p className="modal-result-title">
          {result.kind === "delete-error"
            ? "Could not delete tier"
            : "Could not update tier"}
        </p>
        <p>{result.message}</p>
      </div>
    ) : (
      <div className="edit-inputs modal-result-step">
        <p className="modal-result-title">Done</p>
      </div>
    );

  const steps = [
    {
      content: (
        <div className="new-harvest-log-wrapper">
          <TierForm
            key={effectiveTier.id}
            ref={tierFormRef}
            onHandleSubmit={handleSubmit}
            activeSub={activeSubscription}
            activeTier={effectiveTier}
          />
        </div>
      ),
      footer: (
        <>
          <div className="buttons-footer">
            <button
              type="button"
              className="btn equal-btns"
              onClick={() => {
                if (tierFormRef.current?.getHasUnsavedChanges()) {
                  stepNavRef.current?.goToStep(UNSAVED_STEP);
                  return;
                }
                handleCancel();
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              form={TIER_FORM_ID}
              className="btn equal-btns"
              disabled={isSubmitting || isDeleting}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </button>
          </div>{" "}
          <button
            type="button"
            className="btn delete-btn"
            onClick={handleRequestDelete}
          >
            Delete
          </button>
        </>
      ),
    },
    {
      content: (
        <div className="edit-inputs">
          <p className="modal-exit-confirm-msg">
            Are you sure you want to exit with unsaved changes?
          </p>
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={() => stepNavRef.current?.goToStep(FORM_STEP)}
          >
            No
          </button>
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleCancel}
          >
            Yes
          </button>
        </div>
      ),
    },
    {
      content: (
        <div className="edit-inputs">
          <p className="modal-exit-confirm-msg">
            Are you sure you want to delete this tier?
          </p>
        </div>
      ),
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleBackFromDeleteConfirm}
            disabled={isDeleting}
          >
            No
          </button>
          <button
            type="button"
            className="btn equal-btns"
            onClick={() => void handleConfirmDelete()}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Yes"}
          </button>
        </div>
      ),
    },
    {
      content: resultStepContent,
      footer: (
        <div className="buttons-footer">
          <button
            type="button"
            className="btn equal-btns"
            onClick={handleCloseFromResult}
          >
            Close
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <h3 className="harvest-add-n-edit-title">
        {activeSubscription?.name}
        <br />
        Edit Tier: {effectiveTier?.name}
      </h3>
      <StepNavPanel ref={stepNavRef} steps={steps} className="modal-step-nav" />
    </>
  );
}
